/**
 * ODŚWIEŻANIE EKRANÓW — jedna polityka, jedno miejsce.
 *
 * ⚠️ POWSTAŁO ZE ZGŁOSZENIA (2026-08-18): „puścimy zmianę na pracowniku, wyskakuje zegarek, że
 * zmiana w toku, ale jak sami nie odświeżymy strony, to to się nigdy nie zmieni. Często muszę
 * robić ctrl+shift+r, żeby wskoczyły nowe automaty". Winne były DWIE rzeczy, i tylko jedna
 * mieszkała w tym pliku:
 *
 *   1. `refetchOnWindowFocus: false` + `staleTime: 30_000` i ZERO odpytywania w tle. Ekran raz
 *      wczytany nie pobierał już nic, dopóki ktoś nie kliknął odświeżenia — a stan zmiany
 *      (`QUEUED` → `SENT` → `APPLIED` → `CONFIRMED`) zmienia się na maszynie klienta, czyli
 *      NIGDY w odpowiedzi na kliknięcie w portalu. Zegarek „w toku" musiał więc wisieć.
 *   2. `index.html` bez `Cache-Control` w nginksie — przeglądarka trzymała starą powłokę
 *      aplikacji i po wdrożeniu podawała stare pliki `assets/`. To naprawia `client/nginx.conf`,
 *      nie ten plik; bez tamtej poprawki `ctrl+shift+r` wracałby po każdym wydaniu.
 *
 * ⚠️ DWA TEMPA, NIE JEDNO. Odpytywanie co kilka sekund KAŻDEGO ekranu przez cały dzień to
 * kilkaset żądań na osobę na godzinę przy stu pięćdziesięciu maszynach i czterdziestu trzech
 * instancjach — za to, żeby lista towarów, która się nie zmienia, była „świeża". Dlatego szybkie
 * tempo dostaje TYLKO ekran, na którym coś faktycznie jest w toku, i tylko dopóki jest.
 *
 * ⚠️ W TLE NIE ODPYTUJEMY (`refetchIntervalInBackground` zostaje domyślnie `false`). Karta
 * schowana za innymi to karta, której nikt nie czyta; po powrocie do niej dane odświeży
 * `refetchOnWindowFocus`, czyli jedno żądanie zamiast setki niewidzianych.
 */

/** Ekran bez niczego w toku: dość, żeby nowy automat czy pracownik pojawił się sam. */
export const ODSTEP_SPOKOJNY = 60_000;

/**
 * Coś jest w toku i czekamy na maszynę klienta.
 *
 * ⚠️ 5 s, A NIE 1 s: konfiguracja jedzie do agenta jego cyklem, a ten pobiera zmiany co ~10 s
 * (`AGENT_CONFIG_DOWN_SEC`). Częstsze pytanie portalu nie przyspieszy niczego — doda tylko ruch.
 */
export const ODSTEP_W_TOKU = 5_000;

/** Stany zmiany, które jeszcze nie doszły na maszynę — dopóki są, ekran chodzi szybko. */
const W_TOKU = new Set(['QUEUED', 'SENT', 'APPLIED']);

export const stanWToku = (status: string | null | undefined): boolean =>
  status !== null && status !== undefined && W_TOKU.has(status);

/**
 * Odstęp dla listy, która MOŻE mieć wiersze w toku.
 *
 * `czyWToku` liczy się z DANYCH, które już mamy — więc odstęp sam przyspiesza, gdy zmiana
 * wchodzi do kolejki, i sam zwalnia, gdy wszystko jest potwierdzone. Nikt nie musi tego pilnować.
 */
export const odstep = (czyWToku: boolean): number => (czyWToku ? ODSTEP_W_TOKU : ODSTEP_SPOKOJNY);
